import cloneDeep from "../../utilities/cloneDeep.ts";
import { defined } from "../../utilities/typeHelper.ts";
import {
  ConditionConfiguration,
  ConditionDescriptor,
  ConditionField,
  DimensionDescriptor,
  DimensionField,
  MeasureDateRange,
  PivotGeneralSettings,
  ReportField,
  TabularGeneralSettings,
  TileSettings,
} from "../api/biClient.types.ts";
import { isAggregationBased, isDimension, isMeasure } from "./fieldsHelper.ts";
import { getValidConditionDescriptors } from "./getValidConditions.ts";
import { toAmountType, toCalculateBy } from "./measureConverter.ts";

export enum InvalidConfigurationReason {
  ConditionsFieldAndCustomConditions = 1,
  FieldAndCustomConditions = 2,
  MultipleFields = 3,
  MultipleCustomConditions = 4,
  NoDateFields = 5,
  MandatoryFieldIsNotSelected = 6,
}

export function isTabularConfigurationValid(
  conditions: ConditionField[],
  fields: ReportField[],
  dimensions: DimensionDescriptor[]
): MeasureValidReturnType & { measure?: string } {
  if (fields.length === 0) return { valid: false };
  const validConditions = getValidConditionDescriptors(conditions);
  const dimensionFields = getDimensionFields(fields);
  const measureFields = getMeasureFields(fields, conditions);

  const invalidMeasure = measureFields
    .map((mf) => {
      const valid = isMeasureValid(validConditions, dimensionFields, mf, dimensions);
      return { ...valid, measure: mf.guid };
    })
    .find((result) => result.valid === false);

  if (invalidMeasure !== undefined) {
    return invalidMeasure;
  }
  if (
    !areMandatoryFieldsSet(
      conditions,
      fields.flatMap((f) => f.config.customConditions || [])
    )
  ) {
    return { valid: false, reason: InvalidConfigurationReason.MandatoryFieldIsNotSelected };
  }

  return { valid: true };
}

export const isTileConfigurationValid = (
  conditions: ConditionField[],
  fields: ReportField[],
  dimensions: DimensionDescriptor[]
): MeasureValidReturnType & { measure?: string } => {
  return isTabularConfigurationValid(conditions, fields, dimensions);
};

export function isPivotConfigurationValid(
  conditions: ConditionField[],
  fields: DimensionField[],
  measures: ReportField[],
  dimensions: DimensionDescriptor[]
): MeasureValidReturnType & { measure?: string } {
  if (fields.length === 0) return { valid: false };
  const validConditions = getValidConditionDescriptors(conditions);
  const measureFields = measures.map((m) => getMeasureField(conditions, m));

  const invalidMeasure = measureFields
    .map((mf) => {
      const valid = isMeasureValid(validConditions, fields, mf, dimensions);
      return { ...valid, measure: mf.guid };
    })
    .find((result) => result.valid === false);

  if (invalidMeasure !== undefined) {
    return invalidMeasure;
  }

  if (
    !areMandatoryFieldsSet(
      conditions,
      measures.flatMap((f) => f.config.customConditions || [])
    )
  ) {
    return { valid: false, reason: InvalidConfigurationReason.MandatoryFieldIsNotSelected };
  }
  return { valid: true };
}

export function isMeasureConfigurationValid(
  conditions: ConditionField[],
  fields: DimensionField[],
  dimensions: DimensionDescriptor[],
  value: ReportField
): MeasureValidReturnType {
  const validConditions = getValidConditionDescriptors(conditions);
  const measureField = getMeasureField(conditions, value);

  const valid = isMeasureValid(validConditions, fields, measureField, dimensions);
  return valid;
}

export function isMeasureValid(
  conditions: ConditionDescriptor[],
  fields: DimensionField[],
  calculatedField: CalculatedField,
  dimensions: DimensionDescriptor[]
): MeasureValidReturnType {
  const conditionDates = getDateConditions(conditions, dimensions);
  const dateFields = getDateFields(fields);
  const customConditionDate = getDateCustomConditions(calculatedField, dimensions);

  if (dateFields.length > 0) {
    const referenceDateField = getDimensionDescriptor(defined(dateFields[0]).meta.name, dimensions);
    if (customConditionDate.some((ccd) => ccd.dimensionName === referenceDateField.name)) {
      return { valid: false, referenceDateField, reason: InvalidConfigurationReason.FieldAndCustomConditions };
    }
    return { valid: true, referenceDateField };
  }

  if (conditionDates.length > 0) {
    const referenceDateField = getDimensionDescriptor(defined(conditionDates[0]).dimensionName, dimensions);
    return { valid: true, referenceDateField };
  }

  if (customConditionDate.length > 0) {
    const referenceDateField = getDimensionDescriptor(defined(customConditionDate[0]).dimensionName, dimensions);
    if (customConditionDate.length === 1) return { valid: true, referenceDateField };
    return { valid: false, referenceDateField, reason: InvalidConfigurationReason.MultipleCustomConditions };
  }

  if (calculatedField.dateRangeType === MeasureDateRange.NetChange) return { valid: true };

  return { valid: false, reason: InvalidConfigurationReason.NoDateFields };
}

function getDateConditions(conditions: ConditionDescriptor[], dimensions: DimensionDescriptor[]) {
  return conditions.filter((c) => {
    const meta = dimensions.find((d) => d.name === c.dimensionName);
    if (!meta) {
      return false;
    }
    return meta.options?.asDateReference === true;
  });
}

function getDateFields(fields: DimensionField[]) {
  return fields.filter((f) => f.meta.options?.asDateReference === true);
}

function getDateCustomConditions(field: CalculatedField, dimensions: DimensionDescriptor[]) {
  if (!field.conditions) return [];
  return field.conditions.filter((c) => {
    const meta = dimensions.find((d) => d.name === c.dimensionName);
    if (!meta) {
      return false;
    }
    return meta.options?.asDateReference === true;
  });
}

export function getDimensionFields(fields: ReportField[]) {
  return fields.filter(isDimension);
}

function getMeasureFields(fields: ReportField[], conditions: ConditionField[]) {
  return fields.filter(isAggregationBased).map((measure) => getMeasureField(conditions, measure));
}

function getDimensionDescriptor(name: string, dimensions: DimensionDescriptor[]) {
  return defined(dimensions.find((d) => d.name === name));
}

function getMeasureField(conditions: ConditionField[], field: ReportField): CalculatedField {
  const measureOptions = getMeasure(conditions, field);
  return measureOptions;
}

function getMeasure(conditions: ConditionField[], measure: ReportField): CalculatedField {
  const customConditions = getCustomConditions(measure);
  const linkedConditions = getLinkedConditions(measure, conditions);
  const result = mergeConditions(customConditions, linkedConditions);

  return {
    guid: measure.config.guid,
    dateRangeType: measure.config.dateRange || MeasureDateRange.EndingBalance,
    conditions: result,
  };
}

export function getMeasureCalculateByField(measure: ReportField) {
  if (isMeasure(measure)) {
    return measure.config.calculateByField || toCalculateBy(measure.meta.units);
  }
  return measure.config.calculateByField;
}

export function getMeasureAmountTypeField(measure: ReportField) {
  if (isMeasure(measure)) {
    return measure.config.amountType || toAmountType(measure.meta.units);
  }
  return measure.config.amountType;
}

function getCustomConditions(value: ReportField) {
  if (!value.config.customConditions) return [];
  return value.config.customConditions
    .filter((cc) => cc.filter.values.length > 0)
    .map((cc): ConditionDescriptor => cc.filter);
}

function getLinkedConditions(value: ReportField, conditions: ConditionField[]) {
  if (!value.config.linkedConditions) return [];
  return value.config.linkedConditions
    .map((lc) => conditions.find((c) => c.config.guid === lc)?.config.filter)
    .filter(
      (filter): filter is ConditionDescriptor =>
        (filter && filter && filter.values && filter.values.length > 0) || false
    )
    .map((c) => cloneDeep(c));
}

function mergeConditions(custom: ConditionDescriptor[], linked: ConditionDescriptor[]) {
  const allConditions = [...custom, ...linked];
  return allConditions;
}

function filterUnsetMandatoryField(config: ConditionConfiguration) {
  return config.mandatory && config.filter.values.length === 0;
}

function areMandatoryFieldsSet(conditions: ConditionField[], customConditions: ConditionConfiguration[]) {
  return (
    conditions.filter((c) => filterUnsetMandatoryField(c.config)).length === 0 &&
    customConditions.filter(filterUnsetMandatoryField).length === 0
  );
}

export type MeasureValidReturnType = {
  valid: boolean;
  referenceDateField?: DimensionDescriptor;
  reason?: InvalidConfigurationReason;
};

export type ConfigurationValidReturnType = ReturnType<typeof isTabularConfigurationValid>;

export function createTileDefaultSettings(): TileSettings {
  return {
    compareWithPreviousPeriod: false,
    showGrowth: false,
    periodType: "LastMonth",
  };
}
export function createTabularDefaultSettings(): TabularGeneralSettings {
  return {
    groupSummaryPlace: "Footer",
    showGrandTotal: false,
    subtotalLabelFormatPrefix: "",
    subtotalLabelFormatSuffix: "",
    hideBlankRows: false,
    hideZeroColumns: false,
    hideZeroRows: false,
    hideBlankColumns: false,
    showGroupedColumns: false,
    showNonZeroBalanceAccounts: false,
    subtotalLabelPosition: "None",
    freezedColumns: [],
  };
}

export function createPivotDefaultSettings(): PivotGeneralSettings {
  return {
    rowsGrandTotal: false,
    hideBlankRows: false,
    hideBlankColumns: false,
    hideZeroRows: false,
    hideZeroColumns: false,
  };
}

export type CalculatedField = {
  guid: string;
  dateRangeType: MeasureDateRange;
  conditions: ConditionDescriptor[];
};
